import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { IconButton } from "@fluentui/react";

const BOTTOM_THRESHOLD = 1;

interface ChatScrollContainerProps {
  children: React.ReactNode;
  resetKey: number;
  style: React.CSSProperties;
}

const ChatScrollContainer: React.FC<ChatScrollContainerProps> = ({ children, resetKey, style }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const followingLatestRef = useRef(true);
  const lastScrollTopRef = useRef(0);
  const touchYRef = useRef<number | null>(null);
  const scrollingDownRef = useRef(false);
  const pointerDownRef = useRef(false);
  const [showJumpToLatest, setShowJumpToLatest] = useState(false);

  const readScrollPosition = React.useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    const scrollTop = container.scrollTop;
    const maxScrollTop = Math.max(0, container.scrollHeight - container.clientHeight);
    // Layout shrinkage clamps scrollTop without the reader scrolling upward.
    const previousScrollTop = Math.min(lastScrollTopRef.current, maxScrollTop);
    const distanceFromBottom = maxScrollTop - scrollTop;
    if (scrollTop < previousScrollTop) {
      followingLatestRef.current = false;
      scrollingDownRef.current = false;
    } else if (
      scrollTop > previousScrollTop &&
      distanceFromBottom <= BOTTOM_THRESHOLD &&
      (scrollingDownRef.current || pointerDownRef.current)
    ) {
      followingLatestRef.current = true;
    }
    lastScrollTopRef.current = scrollTop;
    setShowJumpToLatest(distanceFromBottom > BOTTOM_THRESHOLD);
  }, []);

  const updateScroll = React.useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    // A render or resize can run before a pending native scroll event.
    readScrollPosition();
    if (followingLatestRef.current) {
      container.scrollTop = container.scrollHeight;
      lastScrollTopRef.current = container.scrollTop;
      setShowJumpToLatest(false);
    }
  }, [readScrollPosition]);

  useLayoutEffect(() => {
    followingLatestRef.current = true;
    scrollingDownRef.current = false;
    lastScrollTopRef.current = containerRef.current?.scrollTop ?? 0;
  }, [resetKey]);

  useLayoutEffect(() => {
    updateScroll();
  }, [children, resetKey, updateScroll]);

  useEffect(() => {
    const container = containerRef.current;
    const content = contentRef.current;
    if (!container || !content) return;

    // Markdown, images, and charts can change height after a message renders.
    const observer = new ResizeObserver(updateScroll);
    observer.observe(container);
    observer.observe(content);
    return () => observer.disconnect();
  }, [updateScroll]);

  useEffect(() => {
    const releasePointer = () => {
      pointerDownRef.current = false;
    };
    window.addEventListener("pointerup", releasePointer);
    window.addEventListener("pointercancel", releasePointer);
    window.addEventListener("blur", releasePointer);
    return () => {
      window.removeEventListener("pointerup", releasePointer);
      window.removeEventListener("pointercancel", releasePointer);
      window.removeEventListener("blur", releasePointer);
    };
  }, []);

  const pauseFollowing = () => {
    followingLatestRef.current = false;
    scrollingDownRef.current = false;
  };

  const jumpToLatest = () => {
    followingLatestRef.current = true;
    lastScrollTopRef.current = containerRef.current?.scrollTop ?? 0;
    updateScroll();
    containerRef.current?.focus({ preventScroll: true });
  };

  return (
    <div style={{ position: "relative", display: "flex", flex: 1, minHeight: 0 }}>
      <div
        ref={containerRef}
        role="region"
        aria-label="Chat conversation"
        tabIndex={0}
        onScroll={readScrollPosition}
        onWheelCapture={(event) => {
          if (event.deltaY < 0) pauseFollowing();
          else if (event.deltaY > 0) scrollingDownRef.current = true;
        }}
        onPointerDown={(event) => {
          if (event.target === event.currentTarget) pointerDownRef.current = true;
        }}
        onTouchStartCapture={(event) => {
          touchYRef.current = event.touches[0]?.clientY ?? null;
        }}
        onTouchMoveCapture={(event) => {
          const touchY = event.touches[0]?.clientY;
          if (touchY === undefined) return;
          if (touchYRef.current !== null && touchY > touchYRef.current) pauseFollowing();
          else if (touchYRef.current !== null && touchY < touchYRef.current) scrollingDownRef.current = true;
          touchYRef.current = touchY;
        }}
        onTouchEndCapture={() => {
          touchYRef.current = null;
        }}
        onTouchCancelCapture={() => {
          touchYRef.current = null;
        }}
        onKeyDownCapture={(event) => {
          if (event.target !== event.currentTarget) return;
          if (["ArrowUp", "PageUp", "Home"].includes(event.key) || (event.key === " " && event.shiftKey)) {
            pauseFollowing();
          } else if (["ArrowDown", "PageDown", "End", " "].includes(event.key)) {
            scrollingDownRef.current = true;
          }
        }}
        style={{ ...style, minHeight: 0, scrollBehavior: "auto", overflowAnchor: "none" }}
      >
        <div ref={contentRef} style={{ display: "flex", flexDirection: "column", flexShrink: 0 }}>
          {children}
        </div>
      </div>
      {showJumpToLatest && (
        <IconButton
          iconProps={{ iconName: "Down" }}
          ariaLabel="Jump to latest response"
          title="Jump to latest response"
          onClick={jumpToLatest}
          styles={{
            root: {
              position: "absolute",
              bottom: 12,
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 11,
              width: 36,
              height: 36,
              borderRadius: "50%",
              border: "1px solid #d1d1d1",
              backgroundColor: "#ffffff",
              color: "#424242",
              boxShadow: "0 2px 6px rgba(0, 0, 0, 0.14)"
            },
            rootHovered: { backgroundColor: "#f5f5f5" },
            rootPressed: { backgroundColor: "#ebebeb" }
          }}
        />
      )}
    </div>
  );
};

export default ChatScrollContainer;
